import type { MatchFoundPayload } from '@minigame-race/contracts';
import { useEffect, useRef, useState } from 'react';
import { Avatar, Screen } from '../components/Chrome.js';
import { MINIGAME_META } from '../race/registry.js';
import { color } from '../theme.js';
import { play as playSound } from '../lib/sound.js';

/** 3-2-1. `startsAt` is skew-corrected to this browser's clock in useGame. */
export function CountdownScreen({
  match,
  startsAt,
}: {
  match: MatchFoundPayload;
  startsAt: number;
}) {
  const [n, setN] = useState(() => Math.max(0, Math.ceil((startsAt - Date.now()) / 1000)));
  const lastBeep = useRef<number | null>(null);

  useEffect(() => {
    const t = setInterval(() => {
      setN(Math.max(0, Math.ceil((startsAt - Date.now()) / 1000)));
    }, 60);
    return () => clearInterval(t);
  }, [startsAt]);

  // Beep on each new second (3-2-1), a rising "go" at zero. Guarded so the 60ms
  // interval doesn't retrigger the same number.
  useEffect(() => {
    if (lastBeep.current === n) return;
    lastBeep.current = n;
    if (n > 0) playSound('beep');
    else playSound('go');
  }, [n]);

  const eloGap = Math.abs(match.you.elo - match.opponent.elo);

  return (
    <Screen brackets="diagonal">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          gap: 'clamp(16px,3.4cqmin,34px)',
        }}
      >
        <span style={{ fontSize: 13, letterSpacing: '.4em', fontWeight: 600, color: 'rgba(166,255,46,.7)' }}>MATCH FOUND</span>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(14px,3.4cqmin,40px)', width: 'min(94cqw,720px)' }}>
          <PlayerCard name={match.you.displayName} label="YOU" elo={match.you.elo} side="you" />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
            <span style={{ fontWeight: 800, fontSize: 'clamp(20px,4.6cqmin,38px)', color: 'rgba(234,255,234,.85)', animation: 'cdVs 1.4s ease-in-out infinite' }}>VS</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '.12em', color: 'rgba(234,255,234,.35)' }}>Δ {eloGap} ELO</span>
          </div>
          <PlayerCard name={match.opponent.displayName} label={match.opponent.displayName.toUpperCase()} elo={match.opponent.elo} side="opponent" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <span
            key={n}
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontWeight: 800,
              fontSize: 'clamp(80px,22cqmin,200px)',
              lineHeight: 0.9,
              color: color.lime,
              animation: 'cdPop 1s ease-in-out, cdGlow 1s ease-in-out infinite',
            }}
          >
            {n > 0 ? n : 'GO'}
          </span>
          <span style={{ fontSize: 13, letterSpacing: '.34em', fontWeight: 600, color: 'rgba(234,255,234,.5)' }}>
            {n > 0 ? 'GET READY' : ''}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11 }}>
          <span style={{ fontSize: 11, letterSpacing: '.24em', fontWeight: 600, color: 'rgba(234,255,234,.4)' }}>
            THIS RACE · {match.recipe.length} MINIGAME{match.recipe.length === 1 ? '' : 'S'}
            {!match.ranked && ' · UNRANKED'}
          </span>
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', justifyContent: 'center' }}>
            {match.recipe.map((id, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 13px',
                  border: i === 0 ? '1px solid rgba(166,255,46,.3)' : '1px solid rgba(234,255,234,.12)',
                  borderRadius: 999,
                  background: i === 0 ? 'rgba(166,255,46,.06)' : 'rgba(255,255,255,.03)',
                }}
              >
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 11, color: i === 0 ? color.lime : 'rgba(234,255,234,.5)' }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: '.03em', color: i === 0 ? color.text : 'rgba(234,255,234,.75)' }}>
                  {MINIGAME_META[id].title}
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', border: '1px dashed rgba(234,255,234,.2)', borderRadius: 999, background: 'rgba(255,255,255,.02)' }}>
              <span style={{ fontSize: 13 }}>⚑</span>
              <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: '.03em', color: 'rgba(234,255,234,.6)' }}>FINISH</span>
            </div>
          </div>

          {/* Per-game caveats, shown before the clock starts. Currently just the
              Wordle "no diacritics" rule, which players need up front. */}
          {[...new Set(match.recipe)]
            .map((id) => MINIGAME_META[id].note)
            .filter((n): n is string => Boolean(n))
            .map((note) => (
              <span
                key={note}
                style={{
                  fontSize: 11,
                  letterSpacing: '.14em',
                  fontWeight: 600,
                  color: 'rgba(255,176,32,.8)',
                  textAlign: 'center',
                }}
              >
                {note}
              </span>
            ))}
        </div>
      </div>
    </Screen>
  );
}

function PlayerCard({ name, label, elo, side }: { name: string; label: string; elo: number; side: 'you' | 'opponent' }) {
  const tint = side === 'you' ? color.lime : color.cyan;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <Avatar name={name} side={side} size={88} />
      <span style={{ fontWeight: 700, fontSize: 'clamp(13px,2.6cqmin,20px)', letterSpacing: '.05em', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 13, color: tint }}>{elo}</span>
    </div>
  );
}
